import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, MapPin, KeyRound, Clock, FileCheck, AlertTriangle, Bug, Users, Database, Cpu, CreditCard, Mail } from "lucide-react";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";

const SECTIONS = [
  {
    id: "hosting", icon: MapPin, title: "Data hosting & residency",
    body: "All data is hosted exclusively in UK data centres (AWS London, eu-west-2). Data never leaves the UK. We are GDPR Article 46 compliant for international transfers — we simply don't make any. Our infrastructure providers hold ISO 27001 and SOC 2 Type II certifications.",
  },
  {
    id: "encryption", icon: KeyRound, title: "Encryption",
    body: "AES-256 encryption at rest, TLS 1.3 in transit (we reject TLS 1.0/1.1). Sensitive fields including ID numbers, document contents, and e-signature data carry field-level encryption on top of database-level encryption. Per-firm encryption keys with 90-day automatic rotation. Old keys retained for historical decryption.",
  },
  {
    id: "access", icon: Users, title: "Authentication & access",
    body: "Mandatory 2FA (TOTP) for Firm Admin and Compliance Manager. Role-based access control enforced on every API call (no UI-only restrictions). Session idle timeout 30 min with warning. Brute force protection: 5 fails / 10 min = 15 min lockout; 3 lockouts in 24h = account suspension.",
  },
  {
    id: "audit", icon: Clock, title: "Immutable audit trail",
    body: "Every authentication event, every flow change, every compliance decision is recorded with a SHA-256 hash chained to the previous record. Mathematically impossible to alter without breaking the chain. Retention: minimum 7 years per FCA standards. Cannot be deleted by anyone — including our own engineers.",
  },
  {
    id: "compliance", icon: FileCheck, title: "Compliance & certifications",
    body: "Registered with the ICO (registration number pending publication). GDPR compliant. Working towards Cyber Essentials Plus certification. ISO 27001 and SOC 2 Type II on our 2026 roadmap. Our default Data Processing Agreement is available to download from your firm settings.",
  },
  {
    id: "incident", icon: AlertTriangle, title: "Incident response",
    body: "24-hour customer breach notification (vs 72-hour ICO requirement). Documented incident response process with defined RACI. For security concerns: security@compliche.com. PGP key fingerprint published on this page (placeholder).",
  },
  {
    id: "disclosure", icon: Bug, title: "Vulnerability disclosure",
    body: "We welcome responsible disclosure of security issues. Report to security@compliche.com. PGP-encrypted reports accepted. Acknowledgement within 24h, initial assessment within 5 business days. Hall of fame for confirmed researchers.",
  },
];

const SUBPROCESSORS = [
  { name: "Compliance Provider", purpose: "Compliance scanning of submitted text", data: "Submitted scan text only — no PII routinely sent", location: "EU/UK-routed; DPA in place" },
  { name: "Stripe", purpose: "Subscription billing & payment processing", data: "Firm name, billing email, card token", location: "Ireland (EU)" },
  { name: "Resend", purpose: "Transactional email delivery", data: "Recipient email, message content", location: "United States (DPA in place)" },
  { name: "AWS (London)", purpose: "Hosting & object storage", data: "All application data", location: "United Kingdom (eu-west-2)" },
];

export default function SecurityPage() {
  return (
    <div className="bg-white">
      <MarketingNav />
      <section className="bg-navy-900 text-white pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-50" />
        <div className="absolute inset-0 hero-glow opacity-50" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <span className="badge2" style={{ background: "rgba(16,185,129,0.16)", color: "#A7F3D0", borderColor: "rgba(16,185,129,0.4)" }}>
            <ShieldCheck size={12} /> SECURITY DOCUMENTATION
          </span>
          <h1 className="t-display mt-5 leading-tight">Security you can explain to your <span style={{ color: "#10B981" }}>FCA supervisor.</span></h1>
          <p className="mt-6 t-body-lg text-white/70 max-w-2xl mx-auto">
            Compliche handles regulated financial data. Every architectural decision starts with "what does the FCA need to see?" — not "what's easiest for us to build?"
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {SECTIONS.map((s, i) => (
            <div key={s.id} id={s.id} className="security-card p-6" data-testid={`security-section-${s.id}`}>
              <s.icon size={22} className="text-emerald2-dark mb-3" />
              <h3 className="t-h3 mb-2">{s.title}</h3>
              <p className="t-body text-slate2">{s.body}</p>
            </div>
          ))}
        </div>

        <section className="mb-16">
          <h2 className="t-h1 mb-3">Sub-processors</h2>
          <p className="t-body text-slate2 mb-6 max-w-2xl">A full list of third-party services we use to deliver Compliche, what data they process, and where it's processed.</p>
          <div className="card2 overflow-hidden">
            <table className="w-full">
              <thead className="bg-surface border-b border-border2"><tr className="text-left">
                <th className="px-5 py-3 t-label text-slate2">Provider</th>
                <th className="px-5 py-3 t-label text-slate2">Purpose</th>
                <th className="px-5 py-3 t-label text-slate2">Data processed</th>
                <th className="px-5 py-3 t-label text-slate2">Location</th>
              </tr></thead>
              <tbody className="divide-y divide-border2">
                {SUBPROCESSORS.map((p) => (
                  <tr key={p.name}>
                    <td className="px-5 py-4 t-body font-medium">{p.name}</td>
                    <td className="px-5 py-4 t-body-sm">{p.purpose}</td>
                    <td className="px-5 py-4 t-body-sm">{p.data}</td>
                    <td className="px-5 py-4 t-body-sm">{p.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card2 p-8 bg-surface mb-16">
          <h2 className="t-h2 mb-3">Have a security question?</h2>
          <p className="t-body text-slate2 mb-5">We respond to security questionnaires from regulated firms within 48 hours. Compliance teams welcome.</p>
          <div className="flex flex-wrap gap-2">
            <a href="mailto:security@compliche.com" className="btn btn-primary"><Mail size={14} />security@compliche.com</a>
            <Link to="/signup" className="btn btn-secondary">Start free trial</Link>
          </div>
        </section>
      </div>

      <MarketingFooter />
    </div>
  );
}
